import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
} from '@mui/material';

import {
  Add,
  Edit,
  Delete,
  Search,
  Inventory,
  Warning,
  CheckCircle,
  Error,
  Visibility,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { materialsService, Material as MaterialType } from '../services/materialsService';

// Using Material type from service
type Material = MaterialType;

const Materials: React.FC = () => {
  const navigate = useNavigate();
  const [materials, setMaterials] = useState<Material[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [materialToDelete, setMaterialToDelete] = useState<Material | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Fetch materials data from API
  useEffect(() => {
    fetchMaterials();
  }, []);

  const fetchMaterials = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await materialsService.getAll({
        search: searchTerm,
        sortBy: 'name',
        sortOrder: 'asc'
      });
      // Ensure response is an array
      const materialsData = response.data || response;
      setMaterials(Array.isArray(materialsData) ? materialsData : []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch materials');
      console.error('Error fetching materials:', err);
    } finally {
      setLoading(false);
    }
  };

  // Refetch when search term changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchMaterials();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const handleDeleteClick = (material: Material) => {
    setMaterialToDelete(material);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!materialToDelete) return;

    try {
      await materialsService.delete(materialToDelete._id || materialToDelete.id!);
      setSnackbar({ open: true, message: 'Material deleted successfully', severity: 'success' });
      fetchMaterials(); // Refresh the list
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to delete material', severity: 'error' });
    } finally {
      setDeleteDialogOpen(false);
      setMaterialToDelete(null);
    }
  };

  const handleReorder = async (material: Material) => {
    try {
      const reorderQuantity = material.maxStock - material.currentStock;
      await materialsService.reorder(material._id || material.id!, reorderQuantity);
      setSnackbar({ open: true, message: 'Material reorder initiated successfully', severity: 'success' });
      fetchMaterials(); // Refresh the list
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to reorder material', severity: 'error' });
    }
  };

  const getStatusColor = (status: Material['status']) => {
    switch (status) {
      case 'in-stock':
        return 'success';
      case 'low-stock':
        return 'warning';
      case 'out-of-stock':
        return 'error';
      case 'on-order':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: Material['status']) => {
    switch (status) {
      case 'in-stock':
        return <CheckCircle />;
      case 'low-stock':
        return <Warning />;
      case 'out-of-stock':
        return <Error />;
      case 'on-order':
        return <Inventory />;
      default:
        return undefined;
    }
  };

  // Ensure materials is an array before using array methods
  const materialsArray = Array.isArray(materials) ? materials : [];
  
  // Materials are already filtered by the API based on search term
  const filteredMaterials = materialsArray;

  const deriveUnitCost = (item: any) => {
    // Support both top-level unitCost and nested pricing.unitCost
    return (item.unitCost ?? item.pricing?.unitCost ?? 0);
  };

  const deriveStatus = (item: any) => {
    // Normalize status to expected chips: map backend statuses to frontend variants
    const raw = (item.status || '').toLowerCase();
    if (raw === 'active') return 'in-stock';
    if (raw === 'out of stock') return 'out-of-stock';
    if (raw === 'backordered') return 'on-order';
    if (raw === 'discontinued') return 'out-of-stock';
    return item.status || 'in-stock';
  };

  const deriveSupplierName = (item: any) => {
    // Prefer populated primary supplier name; fallback to string or placeholder
    const primary = item?.supplier?.primary;
    if (typeof primary === 'object') {
      return primary?.name || '—';
    }
    if (typeof item?.supplier === 'string') {
      return item.supplier;
    }
    return '—';
  };

  const deriveLocationText = (item: any) => {
    // Support both string location and nested warehouse/zone/aisle/shelf
    const loc = item?.location;
    if (typeof loc === 'string') return loc;
    if (loc && typeof loc === 'object') {
      const parts = [loc.warehouse, loc.zone, loc.aisle, loc.shelf]
        .filter(Boolean)
        .map((p: string) => p.trim())
        .filter((p: string) => p.length > 0);
      return parts.length ? parts.join(' / ') : '—';
    }
    return '—';
  };

  const totalValue = materialsArray.reduce((sum, item) => sum + ((item.currentStock || 0) * deriveUnitCost(item)), 0);
  const inStockCount = materialsArray.filter(item => deriveStatus(item) === 'in-stock').length;
  const lowStockCount = materialsArray.filter(item => deriveStatus(item) === 'low-stock').length;
  const outOfStockCount = materialsArray.filter(item => deriveStatus(item) === 'out-of-stock').length;

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading materials...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={fetchMaterials}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Materials Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/materials/create')}
        >
          Add Material
        </Button>
      </Box>

      {/* Summary Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Total Materials
            </Typography>
            <Typography variant="h5" component="div">
              {materialsArray.length}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              In Stock
            </Typography>
            <Typography variant="h5" component="div" color="success.main">
              {inStockCount}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Low Stock
            </Typography>
            <Typography variant="h5" component="div" color="warning.main">
              {lowStockCount}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Total Value
            </Typography>
            <Typography variant="h5" component="div">
              ${totalValue.toLocaleString()}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Search */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search materials..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Materials Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Current Stock</TableCell>
              <TableCell>Unit</TableCell>
              <TableCell>Unit Cost</TableCell>
              <TableCell>Total Value</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Supplier</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredMaterials.map((item) => (
              <TableRow key={item._id || item.id} hover>
                <TableCell>
                  <Typography variant="subtitle2">{item.name}</Typography>
                </TableCell>
                <TableCell>{item.category}</TableCell>
                <TableCell>
                  <Typography
                    color={item.currentStock <= item.minStock ? 'error' : 'inherit'}
                  >
                    {item.currentStock}
                  </Typography>
                </TableCell>
                <TableCell>{item.unit}</TableCell>
                <TableCell>${deriveUnitCost(item).toFixed(2)}</TableCell>
                <TableCell>${(item.currentStock * deriveUnitCost(item)).toFixed(2)}</TableCell>
                <TableCell>
                  <Chip
                    icon={getStatusIcon(deriveStatus(item))}
                    label={deriveStatus(item).replace('-', ' ').toUpperCase()}
                    color={getStatusColor(deriveStatus(item))}
                    size="small"
                  />
                </TableCell>
                <TableCell>{deriveSupplierName(item)}</TableCell>
                <TableCell>{deriveLocationText(item)}</TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/materials/${item._id || item.id}`)}
                    title="View Material"
                  >
                    <Visibility />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/materials/${item._id || item.id}`)}
                    title="Edit Material"
                  >
                    <Edit />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    color="error"
                    onClick={() => handleDeleteClick(item)}
                    title="Delete Material"
                  >
                    <Delete />
                  </IconButton>
                  {(item.status === 'low-stock' || item.status === 'out-of-stock') && (
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleReorder(item)}
                      title="Reorder Material"
                    >
                      <Inventory />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredMaterials.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="textSecondary">
            No materials found
          </Typography>
          <Typography color="textSecondary">
            {searchTerm ? 'Try adjusting your search terms' : 'Start by adding your first material'}
          </Typography>
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{materialToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Materials;